import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { Button } from '@hbf/dsl';
import { buildSearchQuery } from '@ha/search-query';
import { useIntl } from 'ha/i18n';
import { Link } from 'ha/ui.legacy/Link';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';
import { Link as RouterLink } from 'ha/components/Link';

import { selectors as AuthLogicSelectors } from 'ha/modules/AuthLogic';
import { useBillingSubscription } from 'ha/modules/BillingSubscription';
import { usePage } from 'ha/modules/Page/contexts/PageContext';
import { useConversionTracker } from 'ha/modules/Analytics/hooks';
import { getIsAuthenticated } from 'ha/modules/AuthLogic/selectors';
import { normalizeDate } from '@ha/date';
import { DesiredDatesPickerTrigger } from 'ha/pages/Listing/containers/DesiredDatesPicker';
import { useUnitTypeId } from 'ha/modules/MultiUnitListings/hooks';
import { useDesiredDates } from '../../hooks/useDesiredDates';
import { openDesiredDatesPicker } from '../../helpers/openDesiredDatesPicker';
import {
  getConversationLink,
  getAdvertiserName,
  getDesiredDates,
  getPathToSearch,
  getIsUnlisted,
  isAvailable as getIsAvailable,
  isAdvertiserOwner,
  getListingId,
  getCountryCode,
  getType,
  getCity,
  getTenantFeeRuleName,
  getListingIsCharitable,
  getIsAdvertiserTrusted,
  getTenantReviewsOverallRating,
} from '../../selectors';

const useStyles = makeStyles()({
  link: {
    textDecoration: 'none',
  },
});

export interface Props {
  className?: string;
  variant?: 'default' | 'advertiser-section';
}

const ListingContactAdvertiserButton: React.FC<Props> = ({
  variant = 'default',
  className,
}) => {
  const { classes } = useStyles();
  const { T, urlResolver } = useIntl();
  const { trackAddToCart } = useConversionTracker();
  const { category: pageName } = usePage();

  const { hasDesiredDates } = useDesiredDates();

  const advertiserName = useSelector(getAdvertiserName);
  const conversationURL = useSelector(getConversationLink);
  const desiredDates = useSelector(getDesiredDates);
  const searchPath = useSelector(getPathToSearch);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isUnlisted = useSelector(getIsUnlisted);
  const isOwner = useSelector(isAdvertiserOwner);
  const isAvailable = useSelector(getIsAvailable);
  const listingId = useSelector(getListingId);
  const countryCode = useSelector(getCountryCode);
  const listingCity = useSelector(getCity);
  const listingType = useSelector(getType);
  const hasConversationAccessSubscription = useSelector(
    AuthLogicSelectors.hasConversationAccessSubscription,
  );
  const tenantFeeRuleName = useSelector(getTenantFeeRuleName);
  const isCharitable = useSelector(getListingIsCharitable);
  const isAdvertiserTrusted = useSelector(getIsAdvertiserTrusted);
  const tenantReviewsOverallRating = useSelector(getTenantReviewsOverallRating);

  const { isUnitTypeId: isTenantMULFlowEnabled } = useUnitTypeId();

  const { billingSubscriptionEnabled } =
    useBillingSubscription(tenantFeeRuleName);

  const { startDate, endDate } = desiredDates;

  const onClick = React.useCallback(() => {
    trackAddToCart(listingId);
  }, [listingId, trackAddToCart]);

  const nextUrl = React.useMemo(() => {
    if (isCharitable) {
      return urlResolver.getContactAdvertiserSupportUkraineUrl(
        isTenantMULFlowEnabled ? `ut${listingId}` : listingId,
        {
          startDate: normalizeDate(startDate) || null,
          endDate: normalizeDate(endDate) || null,
        },
      );
    }

    if (isTenantMULFlowEnabled) {
      return urlResolver.getContactAdvertiserUnitTypeUrl(listingId, {
        startDate: normalizeDate(startDate) || null,
        endDate: normalizeDate(endDate) || null,
      });
    }

    return urlResolver.getContactAdvertiserUrl(listingId, {
      startDate: normalizeDate(startDate) || null,
      endDate: normalizeDate(endDate) || null,
    });
  }, [
    urlResolver,
    startDate,
    endDate,
    isCharitable,
    listingId,
    isTenantMULFlowEnabled,
  ]);

  if (!isOwner && conversationURL) {
    return (
      <Track name="Clicked View conversation" type="button">
        <Link component={RouterLink} to={conversationURL}>
          <Button
            className={className}
            color="primary"
            variant="contained"
            fullWidth
          >
            {T('View conversation')}
          </Button>
        </Link>
      </Track>
    );
  }

  if (!isUnlisted && isAvailable) {
    if (!isCharitable && !hasDesiredDates)
      return variant === 'advertiser-section' ? (
        <DesiredDatesPickerTrigger variant="block">
          {({ onClick: onDesiredDatesPickerClick }) => (
            <Button
              className={className}
              color="primary"
              variant="contained"
              fullWidth
              onClick={onDesiredDatesPickerClick}
              data-test-locator="Landlord Card Check Availability Button"
            >
              {T('Check availability')}
            </Button>
          )}
        </DesiredDatesPickerTrigger>
      ) : (
        <Button
          className={className}
          color="primary"
          variant="contained"
          fullWidth
          onClick={openDesiredDatesPicker}
          data-test-locator="Landlord Card Check Availability Button"
        >
          {T('Check availability')}
        </Button>
      );

    let buttonUrl = isAuthenticated
      ? nextUrl
      : urlResolver.getRegisterUrl(nextUrl);

    if (
      !isCharitable &&
      billingSubscriptionEnabled &&
      !hasConversationAccessSubscription
    ) {
      buttonUrl = urlResolver.getSubscriptionLandingUrl('tenant', nextUrl);
    }

    return (
      <Track
        name="Clicked Contact Advertiser"
        type="button"
        city={listingCity}
        country={countryCode}
        listingtype={listingType}
        page={pageName}
        excellentAdvertiser={isAdvertiserTrusted}
        advertiserAverageRating={
          tenantReviewsOverallRating?.moveInOverallRating ?? 0
        }
      >
        <Link
          component={RouterLink}
          to={buttonUrl}
          skipLangFormat
          onClick={onClick}
          className={classes.link}
        >
          <Button
            className={className}
            color="primary"
            variant="contained"
            fullWidth
            data-test-locator="Landlord Card Contact Button"
          >
            {T('Contact %s', advertiserName)}
          </Button>
        </Link>
      </Track>
    );
  }

  return (
    <Track name="Clicked Show alternatives" type="link">
      <Link
        component={RouterLink}
        to={`${searchPath}${buildSearchQuery({
          startDate,
          endDate,
        })}`}
        skipLangFormat
      >
        <Button
          className={className}
          color="primary"
          variant="contained"
          fullWidth
        >
          {T('Show alternatives')}
        </Button>
      </Link>
    </Track>
  );
};

export { ListingContactAdvertiserButton };
